import { StyledCard, StyledTypography } from "../../assets/css/styledComponent";
import React from "react";
import { useTheme, Box, useMediaQuery, Chip, Button } from "@mui/material";
import { Container } from "@mui/system";
import categoryBusinessCardClass from "./categoryBusinessCard.styles";
import officePlaceHolderImage from "../../assets/images/business-image-placeholder.png";
// import BusinessAddressLink from "../../util/google/businessAddressLink"; // ❌ not used here
import { Link, To } from "react-router-dom";
import ShieldRoundedIcon from "@mui/icons-material/ShieldRounded";
import formatCityStateZip from "../../careComponents/utils/formatCityStateZip";

type Props = {
  business: any;
  url?: string;
  linkOverride?: string | null;
  showBookingTypes?: boolean;
};


const CategoryBusinessCard: React.FC<Props> = ({
  business,
  url,
  linkOverride,
  showBookingTypes = true,
}) => {
  const theme = useTheme();
  const styles = categoryBusinessCardClass(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const imgUrl = business?.images?.[0]?.url ?? officePlaceHolderImage;

  const defaultUrl = `/${business?.url ?? url ?? ""}`;
  const toUrl = linkOverride !== undefined ? linkOverride : defaultUrl;

  const linkTo: To | null =
    typeof toUrl === "string" && toUrl.length > 0
      ? { pathname: toUrl }
      : null;

  const LinkWrap = ({ children }: { children: React.ReactNode }) =>
    linkTo ? (
      <Box
        component={Link}
        to={linkTo}
        state={{ business }}
        sx={{ textDecoration: "none", color: "inherit" }}
      >
        {children}
      </Box>
    ) : (
      <>{children}</>
    );

  // 👇 derive masked address just for this card
  const fullAddress =
    typeof business?.address === "string" && business.address.trim()
      ? business.address.trim()
      : formatCityStateZip(business?.address);

  return (
    <Container maxWidth="lg" sx={styles.root}>
      <StyledCard
        sx={{
          maxWidth: "100%",
          boxShadow: theme.shadows[3],
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* ===== Desktop / Tablet Layout ===== */}
        <Box
          display={{ xs: "none", md: "flex" }}
          justifyContent="flex-start"
          alignItems="flex-start"
          sx={{ gap: "2rem", p: 2 }}
        >
          {/* Image */}
          <LinkWrap>
            <Box
              component="img"
              src={imgUrl}
              alt={business?.name}
              sx={{
                width: "300px",
                minWidth: "160px",
                height: "auto",
                objectFit: "cover",
                borderRadius: "10px",
                display: "block",
              }}
            />
          </LinkWrap>

          {/* Right side content */}
          <Box sx={{ pt: "2px", flex: 1, minWidth: 0 }}>
            <Box sx={{ mb: 1 }}>
              <LinkWrap>
                <StyledTypography sx={{ fontSize: "1.25rem", lineHeight: 1.25 }}>
                  {business?.name}
                </StyledTypography>
              </LinkWrap>

              {/* 👇 ONLY City, ST ZIP (no map link here) */}
              {fullAddress && (
                <StyledTypography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mb: 1, lineHeight: 1.4, wordBreak: "break-word" }}
                >
                  {fullAddress}
                </StyledTypography>
              )}
            </Box>

            {/* Booking types (desktop) */}
            <Box display={{ xs: "none", md: "block" }}>
              {showBookingTypes ? (
                business?.bookingPageUrl ? <Button component="a" href={business.bookingPageUrl} variant="outlined">Book a visit</Button> : null
              ) : null}
            </Box>
          </Box>
        </Box>

        {/* ===== Mobile Layout (overlay caption on image) ===== */}
        {isMobile && (
          <Box>
            <LinkWrap>
              <Box sx={{ display: "block", position: "relative" }}>
                <Box
                  component="img"
                  src={imgUrl}
                  alt={business?.name}
                  sx={{
                    width: "100%",
                    height: "auto",
                    objectFit: "cover",
                    display: "block",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "45%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.65), rgba(0,0,0,0.15))",
                    pointerEvents: "none",
                  }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    left: 12,
                    right: 12,
                    bottom: 10,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.25,
                    color: "#fff",
                    backgroundColor: "rgba(0,0,0,0.55)",
                    backdropFilter: "blur(2px)",
                    borderRadius: "10px",
                    px: 1,
                    py: 0.75,
                  }}
                >
                  <StyledTypography
                    sx={{
                      color: "#fff",
                      fontSize: "1.05rem",
                      lineHeight: 1.25,
                      fontWeight: 600,
                      textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {business?.name}
                  </StyledTypography>

                  {/* 👇 ONLY City, ST ZIP */}
                  {fullAddress && (
                    <StyledTypography
                      variant="body2"
                      sx={{
                        color: "#fff",
                        opacity: 0.95,
                        lineHeight: 1.35,
                        textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {fullAddress}
                    </StyledTypography>
                  )}
                </Box>
              </Box>
            </LinkWrap>

            {/* Booking types (mobile stacked) */}
            <Box sx={{ px: 2, pb: 2, pt: 1.25 }}>
              {showBookingTypes ? (
                business?.bookingPageUrl ? <Button component="a" href={business.bookingPageUrl} variant="outlined">Book a visit</Button> : null
              ) : null}
            </Box>
          </Box>
        )}
      </StyledCard>
    </Container>
  );
};

export default CategoryBusinessCard;
